import { Component, signal, inject, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { UserRole } from '@core/interfaces/role.interface';
import { Reseller as ResellerService } from '@modules/reseller/services/reseller';
import { DataNotFound } from '@shared/data-not-found/data-not-found';
import { Loader } from '@shared/loader/loader';
import { Pagination } from '@shared/pagination/pagination';
import { Title, Toaster } from '@shared/services';

@Component({
  selector: 'app-reseller',
  imports: [CommonModule, Pagination, ReactiveFormsModule, DataNotFound, Loader],
  templateUrl: './reseller.html',
  styleUrl: './reseller.scss'
})
export class Reseller implements OnInit {
  private fb = inject(FormBuilder);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);
  private resellerService = inject(ResellerService);

  isLoading = signal(false);
  isModalOpen = signal(false);
  isDeleteModalOpen = signal(false);
  isEditMode = signal(false);
  showPassword = signal(false);
  isPackageDropdownOpen = signal(false);
  resellers: any = signal([]);
  resellerToDelete: any = signal(null);
  totalItems = signal(0);
  pageSize = signal(5);
  currentPage = signal(1);
  searchTerm = signal('');
  packages: any = signal([]);
  resellerForm: FormGroup = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    company: ['', [Validators.required]],
    country: ['', [Validators.required]],
    mobile: ['', [Validators.required]],
    smsCreditLimit: [0, [Validators.required, Validators.min(0)]],
    package: [[], [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    senderId: ['', [Validators.required]]
  });

  ngOnInit() {
    this.titleService.setTitle('Reseller');
    this.getResellers();
    this.getPackages();
  }

  getResellers() {
    this.isLoading.set(true);
    this.resellers.set([]);

    const queryParams: any = {
      type: UserRole.RESELLER,
      page: this.currentPage(),
      pageSize: this.pageSize()
    };

    if (this.searchTerm()) {
      queryParams.search = this.searchTerm();
    }

    this.resellerService.getResellers(queryParams).subscribe({
      next: (response) => {
        this.resellers.set(response.data);
        this.totalItems.set(response.pagination.total);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
        this.isLoading.set(false);
      }
    });
  }

  getPackages() {
    this.packages.set([]);
    this.resellerService.getPackages().subscribe({
      next: (response) => {
        this.packages.set(response.data);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  onSearch(event: any) {
    this.searchTerm.set(event);
    this.currentPage.set(1);
    this.getResellers();
  }

  isFilterApplied() {
    return this.searchTerm() !== '';
  }

  clearFilter() {
    this.searchTerm.set('');
    this.currentPage.set(1);
    this.getResellers();
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    this.getResellers();
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.getResellers();
  }

  onAddReseller() {
    this.isEditMode.set(false);
    this.resellerForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      country: '',
      mobile: '',
      smsCreditLimit: 0,
      package: [],
      password: '',
      senderId: ''
    });
    this.resellerForm.get('email')?.enable();
    this.resellerForm
      .get('password')
      ?.setValidators([Validators.required, Validators.minLength(6)]);
    this.resellerForm.get('password')?.updateValueAndValidity();
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.isEditMode.set(false);
    this.showPassword.set(false);
    this.isPackageDropdownOpen.set(false);
  }

  togglePackageDropdown(event: Event) {
    event.stopPropagation();
    this.isPackageDropdownOpen.update((v) => !v);
  }

  isPackageSelected(packageId: string): boolean {
    const selected = this.resellerForm.get('package')?.value || [];
    return selected.includes(packageId);
  }

  togglePackageSelection(packageId: string) {
    const currentSelected = this.resellerForm.get('package')?.value || [];
    let newSelected: string[];

    if (currentSelected.includes(packageId)) {
      newSelected = currentSelected.filter((id: string) => id !== packageId);
    } else {
      newSelected = [...currentSelected, packageId];
    }

    this.resellerForm.get('package')?.setValue(newSelected);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInside = target.closest('.custom-multiselect');
    if (!isInside && this.isPackageDropdownOpen()) {
      this.isPackageDropdownOpen.set(false);
    }
  }

  getSelectedPackageNames(): string {
    const selectedIds = this.resellerForm.get('package')?.value || [];
    const selectedNames = this.packages()
      .filter((pkg: any) => selectedIds.includes(pkg.name))
      .map((pkg: any) => pkg.name);

    if (selectedNames.length === 0) {
      return 'Select Packages';
    }
    if (selectedNames.length > 2) {
      return `${selectedNames.slice(0, 2).join(', ')} +${selectedNames.length - 2} more`;
    }
    return selectedNames.join(', ');
  }

  togglePasswordVisibility() {
    this.showPassword.update((v) => !v);
  }

  saveReseller() {
    if (this.resellerForm.invalid) {
      this.resellerForm.markAllAsTouched();
      this.toasterService.errorAlert('Please fill in all required fields correctly');
      return;
    }

    const data = this.resellerForm.getRawValue();
    let userData: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company,
      country: data.country,
      mobile: data.mobile,
      creditLimit: data.smsCreditLimit,
      package: data.package,
      senderId: data.senderId
    };

    if (this.isEditMode()) {
      const existingData = this.resellers().find((reseller: any) => reseller.email === data.email);
      if (!existingData) {
        this.toasterService.errorAlert('No reseller found for update');
        return;
      }

      this.resellerService.updateReseller(existingData._id, userData).subscribe({
        next: () => {
          this.toasterService.successAlert('Reseller updated successfully!');
          this.getResellers();
          this.closeModal();
        },
        error: (error) => {
          this.toasterService.errorAlert(error.error.message);
        }
      });
      return;
    }

    userData = {
      ...userData,
      email: data.email,
      password: data.password,
      role: UserRole.RESELLER
    };

    this.resellerService.addReseller(userData).subscribe({
      next: () => {
        this.toasterService.successAlert('Reseller added successfully!');
        this.getResellers();
        this.closeModal();
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  onEdit(reseller: any) {
    const existingData = this.resellers().find((user: any) => user.email === reseller.email);

    if (!existingData) {
      this.toasterService.errorAlert('No reseller found for update');
      return;
    }

    this.isEditMode.set(true);
    this.resellerForm.patchValue({
      firstName: existingData.firstName,
      lastName: existingData.lastName,
      email: existingData.email,
      company: existingData.company,
      country: existingData.country,
      mobile: existingData.mobile,
      smsCreditLimit: existingData.smsCreditLimit,
      package: existingData.packages,
      senderId: existingData.senderIdNames,
      password: ''
    });
    this.resellerForm.get('email')?.disable();
    this.resellerForm.get('company')?.disable();
    this.resellerForm.get('password')?.clearValidators();
    this.resellerForm.get('password')?.updateValueAndValidity();
    this.isModalOpen.set(true);
  }

  onDelete(reseller: any) {
    this.resellerToDelete.set(reseller);
    this.isDeleteModalOpen.set(true);
  }

  confirmDelete() {
    const reseller = this.resellerToDelete();
    if (!reseller) {
      this.toasterService.errorAlert('No reseller found for delete');
      return;
    }

    this.resellerService.deleteReseller(reseller._id.toString()).subscribe({
      next: () => {
        this.toasterService.successAlert('Reseller deleted successfully!');
        this.getResellers();
        this.closeDeleteModal();
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message || 'Failed to delete reseller');
      }
    });
  }

  closeDeleteModal() {
    this.isDeleteModalOpen.set(false);
    this.resellerToDelete.set(null);
  }
}
